'use client';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useRequest } from 'ahooks';
import { Input, Modal } from 'antd';
import type { InputRef } from 'antd';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Search, User, Video, FileText, Loader2, Trash, X } from 'lucide-react';
import { UserAvatar } from '@/app/components/Lives/UserBase/avatar';
import { useTranslation } from 'react-i18next';
import { api, type TokenDetail } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell } from '@/components/ui/table';
import Token from '@/components/token';
import { smartFormat, truncateToDecimals } from '@/lib/utils';
import { chainList } from '@/app/const/chain';
import { captureSearchExecute } from '@/lib/analytics/posthog-events';
import CoImage from '@/components/co-image';
import { Empty } from '@/components/empty';
import UserTags from '@/app/components/User/Tags';
import SimpleAvatar from '@/app/components/User/SimpleAvatar';

interface SearchResultItem {
  id: string;
  title: string;
  link: string;
  type: 'live' | 'streamer' | 'post';
  avatar?: string;
  streamer?: string;
  isStreamer?: boolean;
  isRoom?: number;
  roomId?: number;
  userId?: number;
  handle?: string;
}

interface SearchResults {
  liveStreams: SearchResultItem[];
  streamers: SearchResultItem[];
  posts: SearchResultItem[];
}

// 搜索记录数据结构
interface SearchHistory {
  live: string[];
  market: string[];
}

export default function CoSearch() {
  // 关键字
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // 搜索相关
  const [searchType, setSearchType] = useState('live');
  const [searchCoinResults, setSearchCoinResults] = useState<TokenDetail[] | null>(null);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const modalInputRef = useRef<InputRef>(null);
  const isMountedRef = useRef(true);

  // 搜索记录缓存
  const [searchHistory, setSearchHistory] = useState<SearchHistory>({ live: [], market: [] });
  const STORAGE_KEY = 'billion-search';

  // 从localStorage读取搜索记录
  const loadSearchHistory = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as SearchHistory;
        setSearchHistory(parsed);
      }
    } catch (error) {
      console.error('Failed to load search history:', error);
    }
  }, []);

  // 保存搜索记录到localStorage
  const saveSearchHistory = useCallback((history: SearchHistory) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
      setSearchHistory(history);
    } catch (error) {
      console.error('Failed to save search history:', error);
    }
  }, []);

  // 添加搜索关键词到记录
  const addToSearchHistory = useCallback(
    (keyword: string, type: 'live' | 'market') => {
      if (!keyword.trim()) return;

      const currentHistory = searchHistory;
      const typeKey = type === 'live' ? 'live' : 'market';
      const currentList = currentHistory[typeKey];

      // 移除已存在的相同关键词
      const filteredList = currentList.filter(item => item !== keyword);

      // 添加新关键词到开头
      const newList = [keyword, ...filteredList].slice(0, 5); // 最多保存5个

      const newHistory = {
        ...currentHistory,
        [typeKey]: newList,
      };

      saveSearchHistory(newHistory);
    },
    [searchHistory, saveSearchHistory]
  );

  // 删除单个搜索记录
  const removeFromSearchHistory = useCallback(
    (keyword: string, type: 'live' | 'market') => {
      const currentHistory = searchHistory;
      const typeKey = type === 'live' ? 'live' : 'market';
      const newList = currentHistory[typeKey].filter(item => item !== keyword);

      const newHistory = {
        ...currentHistory,
        [typeKey]: newList,
      };

      saveSearchHistory(newHistory);
    },
    [searchHistory, saveSearchHistory]
  );

  // 清除所有搜索记录
  const clearSearchHistory = useCallback(
    (type?: 'live' | 'market') => {
      if (type) {
        const newHistory = {
          ...searchHistory,
          [type]: [],
        };
        saveSearchHistory(newHistory);
      } else {
        const newHistory = { live: [], market: [] };
        saveSearchHistory(newHistory);
      }
    },
    [searchHistory, saveSearchHistory]
  );

  const syncSearchQueryToUrl = useCallback(
    (keyword: string) => {
      const params = new URLSearchParams(searchParams);
      const normalizedKeyword = keyword.trim();

      if (normalizedKeyword) {
        params.set('q', normalizedKeyword);
      } else {
        params.delete('q');
      }

      const queryString = params.toString();
      navigate(queryString ? `${pathname}?${queryString}` : pathname, { replace: true });
    },
    [navigate, pathname, searchParams]
  );

  // 根据当前路径设置默认搜索类型
  useEffect(() => {
    const tokenRoutes = ['/markets', '/perps', '/scan', '/swap', '/line'];
    const isTokenRoute = tokenRoutes.some(route => pathname.startsWith(route));

    if (isTokenRoute) {
      setSearchType('markets');
    } else {
      setSearchType('live');
    }
  }, [pathname]);

  // 组件加载时读取搜索记录
  useEffect(() => {
    loadSearchHistory();
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, [loadSearchHistory]);

  // 移除防抖，实现即时搜索
  const { cancel } = useRequest(
    async () => {
      if (searchTerm.trim() === '') {
        if (!isMountedRef.current) return;
        setIsSearchLoading(false);
        setResults(null);
        setSearchCoinResults(null);
        return;
      }
      if (!isMountedRef.current) return;
      setIsSearchLoading(true);

      const currentSearchType = searchType;
      const currentSearchTerm = searchTerm;
      syncSearchQueryToUrl(currentSearchTerm);

      try {
        if (currentSearchType === 'live') {
          const res = await api.common.comprehensiveSearch({ keyword: currentSearchTerm });
          if (!isMountedRef.current) return;

          const { liveRoomList = [], streamerList = [], talkList = [] } = res.data || {};
          const filteredLiveStreams = liveRoomList.slice(0, 3).map(s => ({
            id: s.roomId,
            title: s.roomName,
            link: `/live/detail?id=${s.roomId}`,
            type: 'live' as const,
            avatar: s.avatar,
            streamer: s.userName,
            handle: s.handle,
          }));

          const filteredStreamers = streamerList.slice(0, 3).map(s => ({
            id: s.userId,
            title: s.userName,
            link: !!s.isRoom ? `/live/detail?id=${s.roomId}` : `${s.handle}`,
            type: 'streamer' as const,
            avatar: s.avatar,
            streamer: s.userName,
            isStreamer: s.isStreamer,
            isRoom: s.isRoom,
            roomId: s.roomId,
            userLabels: s?.userLabels,
            handle: s.handle,
          }));
          const filteredPosts = talkList.slice(0, 3).map(p => ({
            id: p.talkId,
            title: p.content,
            link: `/square/detail/${p.talkId}`,
            type: 'post' as const,
            avatar: p?.author?.avatar,
            streamer: p?.author?.name,
            userId: p?.author?.userId,
            handle: p?.author?.handle,
          }));

          console.log(111, filteredPosts);

          if (isMountedRef.current && currentSearchType === searchType && currentSearchTerm === searchTerm) {
            setResults({
              liveStreams: filteredLiveStreams as unknown as SearchResultItem[],
              streamers: filteredStreamers as unknown as SearchResultItem[],
              posts: filteredPosts as unknown as SearchResultItem[],
            });
            addToSearchHistory(currentSearchTerm.trim(), 'live');
            captureSearchExecute({ search_type: 'live', query: currentSearchTerm });
          }
        } else {
          const res = await api.dex.searchToken({ search: currentSearchTerm, chains: chainList.map(c => c.chainIndex).join(',') });
          if (isMountedRef.current && currentSearchType === searchType && currentSearchTerm === searchTerm) {
            setSearchCoinResults(res.data || []);
            addToSearchHistory(currentSearchTerm.trim(), 'market');
            captureSearchExecute({ search_type: 'markets', query: currentSearchTerm });
          }
        }
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        if (isMountedRef.current) {
          setIsSearchLoading(false);
        }
      }
    },
    {
      debounceWait: 300,
      refreshDeps: [searchTerm, searchType],
    }
  );

  // 当 searchType 或 searchTerm 变化时，取消之前的请求
  useEffect(() => {
    return () => {
      cancel();
    };
  }, [searchType, searchTerm, cancel]);

  useEffect(() => {
    if (!isSearchDialogOpen) return;
    const timer = window.setTimeout(() => {
      modalInputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [isSearchDialogOpen]);

  const goToSearch = (searhKey?: string) => {
    if (!searchTerm.trim() && !searhKey) {
      return;
    }
    const keyword = searhKey || searchTerm.trim();
    // 如果当前就是search页面
    if (['/search/', '/search'].includes(window.location.pathname)) {
      // 获取当前type
      const type = searchParams.get('type');
      navigate(`/search?type=${type}&q=${keyword}`);
    } else {
      navigate(`/search?type=live&q=${keyword}`);
    }
    closeSearchDialog();
  };

  const closeSearchDialog = () => {
    setSearchTerm('');
    setIsSearchDialogOpen(false);
  };

  return (
    <div
      className="relative flex md:flex-1 justify-end md:justify-center"
      ref={searchRef}
    >
      <div className="flex w-full justify-end md:justify-center">
        <div
          className="flex h-[34px] w-[140px] cursor-pointer items-center rounded-lg border border-button-border/50 bg-background-card px-3 text-sm text-font-primary/50 transition-all duration-300 dark:border-button-border/85 md:w-[290px] 2xl:w-[310px] max-md:w-[34px] max-md:px-1 max-md:justify-center"
          onClick={() => setIsSearchDialogOpen(true)}
        >
          <Search className="h-4 w-4 shrink-0" />
          <span className='max-md:hidden ml-2'>{t('header.searchPlaceholderAll')}</span>
        </div>
      </div>
      {/* 搜索弹窗 */}
      <Modal 
        open={isSearchDialogOpen} 
        onCancel={closeSearchDialog} 
        footer={null} 
        width={600} 
        centered 
        destroyOnHidden 
        title={t('common.search')}
      >
        <div className="flex max-h-[80vh] flex-col overflow-hidden p-0">
          <div className="space-y-4">
            {/* {process.env.isProd ? null : (
              <div className="flex items-center gap-4 border-b border-button-border/50 pb-2">
                <h3
                  className={`text-sm text-font-primary flex items-center py-1 gap-1 relative ${searchType === 'live' ? 'text-primary-500' : 'hover:text-primary-500 cursor-pointer'}`}
                  onClick={() => {
                    if (searchType === 'live') return;
                    setSearchType('live');
                  }}
                >
                  <Video className="w-4 h-4" />
                  {t('search.material')}
                  {searchType === 'live' && <div className="absolute left-0 w-full h-0.5 bg-primary-500 rounded-sm -bottom-2.5" />}
                </h3>
                <h3
                  className={`text-sm text-font-secondary flex items-center py-1 gap-1 relative ${searchType === 'markets' ? 'text-primary-500' : 'hover:text-primary-500 cursor-pointer'}`}
                  onClick={() => {
                    if (searchType === 'markets') return;
                    setSearchType('markets');
                  }}
                >
                  <ScanSearch className="w-4 h-4" />
                  {t('search.token')}
                  {searchType === 'markets' && <div className="absolute left-0 w-full h-0.5 bg-primary-500 rounded-sm -bottom-2.5" />}
                </h3>
              </div>
            )} */}
            <div className="relative flex items-center mt-2">
              <Input
                ref={modalInputRef}
                placeholder={searchType === 'live' ? t('header.searchPlaceholder') : t('dynamic.cryptoSelector.searchPlaceholder')}
                className="w-full bg-background-card rounded-lg"
                value={searchTerm}
                size="large"
                allowClear
                prefix={<Search className="w-4 h-4" />}
                autoFocus
                onChange={e => setSearchTerm(e.target.value)}
                onCompositionStart={() => {
                  setIsComposing(true);
                }}
                onCompositionEnd={() => {
                  setIsComposing(false);
                }}
                onKeyDown={e => {
                  // 如果按下回车键，且不在中文输入法输入过程中
                  if (e.key === 'Enter' && searchType === 'live' && !isComposing) {
                    goToSearch();
                  }
                }}
              />
            </div>
          </div>
          <div className="mt-4 pb-4 overflow-y-auto h-[70vh]">
            {/* 直播搜索内容 */}
            {searchType === 'live' ? (
              searchTerm && (results || isSearchLoading) ? (
                <>
                  {isSearchLoading ? (
                    <div className="h-40 text-center p-4 text-sm text-font-primary flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('header.loading')}
                    </div>
                  ) : results?.liveStreams.length === 0 && results?.streamers.length === 0 && results?.posts.length === 0 ? (
                    <div className="text-center p-4 text-sm text-font-primary">
                      <p>{t('header.noResults', { keyword: searchTerm })}</p>
                    </div>
                  ) : (
                    <>
                      {results?.liveStreams && results.liveStreams.length > 0 && (
                        <div className="flex flex-col gap-1 mb-4">
                          <h4 className="text-xs font-semibold text-font-primary px-2 py-1 flex items-center">
                            <Video className="w-3 h-3 mr-2" />
                            {t('header.liveRooms')}
                          </h4>
                          {results?.liveStreams.map(item => (
                            <Link
                              key={item.id}
                              to={item.link}
                              className="block rounded-md p-2 transition-colors hover:bg-background-3/10"
                              onClick={closeSearchDialog}
                            >
                              <div className="flex items-center space-x-3">
                                <Avatar className="w-8 h-8">
                                  <AvatarImage src={item.avatar || '/placeholder.svg'} />
                                  <AvatarFallback>{item.streamer?.[0]}</AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="text-sm font-medium text-font-primary truncate">{item.title}</p>
                                  <p className="text-xs text-font-primary">{item.streamer}</p>
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                      {results?.streamers && results.streamers.length > 0 && (
                        <div className="flex flex-col gap-1 mb-4">
                          <h4 className="text-xs font-semibold text-font-primary px-2 py-1 flex items-center">
                            <User className="w-3 h-3 mr-2" />
                            {t('header.users')}
                          </h4>
                          {results?.streamers.map(item => (
                            <Link
                              key={item.id}
                              to={item.link}
                              className="block rounded-md p-2 transition-colors hover:bg-background-3/10"
                              onClick={closeSearchDialog}
                            >
                              <div className="flex items-center space-x-3">
                                <UserAvatar
                                  userInfo={{
                                    userId: item.id,
                                    userName: item.title,
                                    avatar: item.avatar || '',
                                  }}
                                  liveStatus={!!item.isRoom}
                                  size={8}
                                  className="border-4 border-white shadow-lg"
                                  forbidLink={true}
                                />
                                <div className="flex flex-col gap-1">
                                  <div className="flex items-center gap-1">
                                    {item.isStreamer === 1 && (
                                      <Badge variant="default-outline" size="xxs">
                                        {t('Me.profile.broadcaster')}
                                      </Badge>
                                    )}
                                    <p className="text-sm font-medium text-font-primary">{item.title}</p>
                                    <UserTags userLabels={item?.userLabels} />
                                  </div>
                                  <span className="text-xs text-font-primary/50">ID: {item.id}</span>
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                      {results?.posts && results.posts.length > 0 && (
                        <div className="flex flex-col gap-1">
                          <h4 className="text-xs font-semibold text-font-primary px-2 py-1 flex items-center">
                            <FileText className="w-3 h-3 mr-2" />
                            {t('header.posts')}
                          </h4>
                          {results?.posts.map(item => (
                            <div key={item.id} className="block rounded-md p-2 transition-colors hover:bg-background-3/10">
                              <p
                                className="text-sm text-font-primary line-clamp-2"
                                onClick={() => {
                                  closeSearchDialog();
                                  navigate(`/square/detail/${item.id}`);
                                }}
                              >
                                {/* <FilterSearch text={item.title} search={searchTerm} /> */}
                                {item.title}
                              </p>
                              <div
                                className="mt-1 flex items-center gap-2 text-xs cursor-pointer"
                              >
                                <span className='text-font-primary/50'>{t('header.from')}</span>
                                <SimpleAvatar
                                  avatar={item.avatar || ''}
                                  name={item.streamer}
                                  handle={item.handle}
                                  userId={item.userId}
                                  className="gap-1 min-w-0"
                                  avatarClassName="h-4 w-4"
                                  nameClassName="truncate text-font-primary/75 hover:underline hover:text-font-primary underline-offset-2"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : searchHistory.live.length > 0 ? (
                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="flex items-center px-2 py-1 text-xs font-semibold text-font-primary">
                      <Search className="mr-2 h-3 w-3" />
                      {t('header.searchHistory')}
                    </h4>
                    <span
                      className="text-xs text-font-primary/50 flex items-center gap-1 cursor-pointer hover:text-font-primary/85"
                      onClick={() => clearSearchHistory('live')}
                    >
                      <Trash className="w-3 h-3" />
                      {t('header.clearHistory')}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    {searchHistory.live.map((keyword, index) => (
                      <div
                        key={index}
                        className="bg-background-3/5 text-xs text-font-primary/85 rounded-sm px-2 py-1 hover:bg-background-3/10 cursor-pointer relative group"
                        onClick={() => {
                          setSearchTerm(keyword);
                        }}
                      >
                        {keyword}
                        <div
                          className="hidden bg-font-primary/20 w-3 h-3 rounded-full flex items-center justify-center absolute -top-1 -right-1 group-hover:flex"
                          onClick={e => {
                            e.stopPropagation();
                            removeFromSearchHistory(keyword, 'live');
                          }}
                        >
                          <X className="w-2 h-2" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center p-8 text-sm text-font-primary/50">{t('header.searchPlaceholder')}</div>
              )
            ) : (
              /* 代币搜索内容 */
              <>
                {/* 搜索历史记录 - 当 searchTerm 为空且有历史记录时显示 */}
                {!searchTerm && searchHistory.market.length > 0 && (
                  <div className="flex flex-col gap-1 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="flex items-center px-2 py-1 text-xs font-semibold text-font-primary">
                        <Search className="mr-2 h-3 w-3" />
                        {t('header.searchHistory')}
                      </h4>
                      <span
                        className="text-xs text-font-primary/50 flex items-center gap-1 cursor-pointer hover:text-font-primary/85"
                        onClick={() => clearSearchHistory('market')}
                      >
                        <Trash className="w-3 h-3" />
                        {t('header.clearHistory')}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      {searchHistory.market.map((keyword, index) => (
                        <div
                          key={index}
                          className="bg-secondary text-xs text-font-primary/85 rounded-sm px-2 py-1 hover:bg-secondary-hover cursor-pointer relative group"
                          onClick={() => {
                            setSearchTerm(keyword);
                          }}
                        >
                          {keyword}
                          <div
                            className="hidden bg-font-primary/20 w-3 h-3 rounded-full flex items-center justify-center absolute -top-1 -right-1 group-hover:flex"
                            onClick={e => {
                              e.stopPropagation();
                              removeFromSearchHistory(keyword, 'market');
                            }}
                          >
                            <X className="w-2 h-2" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b border-button-border/20">
                        <TableHead className="w-[150px]">{t('dex.table.name')}</TableHead>
                        <TableHead className="w-[150px]">{t('header.network')}</TableHead>
                        <TableHead className="w-[100px]">{t('search.price')}</TableHead>
                        <TableHead className="w-[100px]">{t('dex.table.liquidity')}</TableHead>
                        <TableHead className="w-[150px]">{t('search.marketCap')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {!searchTerm ? (
                        // searchTerm 为空时，只显示表头，不显示任何内容
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-sm text-font-primary/50 py-8">
                            {t('empty.noData')}
                          </TableCell>
                        </TableRow>
                      ) : isSearchLoading ? (
                        // 正在加载时，显示加载状态
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-sm text-font-primary py-8">
                            <div className="flex items-center justify-center gap-2">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              {t('header.loading')}
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : searchCoinResults && searchCoinResults.length > 0 ? (
                        // 有搜索结果时，显示结果
                        searchCoinResults.map((item: TokenDetail) => (
                          <TableRow
                            key={item.tokenSymbol}
                            className="cursor-pointer border-button-border/5 transition-colors hover:bg-background-3/10"
                            onClick={() => {
                              closeSearchDialog();
                              window.open(`/line?chainId=${item.chainIndex}&tokenAddress=${item.tokenContractAddress}`, '_blank');
                            }}
                          >
                            <TableCell className="w-[150px] p-3">
                              <Token
                                tokenContractAddress={item.tokenContractAddress}
                                tokenLogoUrl={item.tokenLogoUrl}
                                tokenName={item.tokenName}
                                tokenSymbol={item.tokenSymbol}
                                decimals={item.decimal}
                                width={32}
                                height={32}
                              />
                            </TableCell>
                            <TableCell className="w-[150px] p-3">
                              {(() => {
                                const chain = chainList.find(c => Number(c.chainIndex) === Number(item.chainIndex));
                                if (chain) {
                                  return (
                                    <div className="flex items-center gap-2">
                                      <CoImage src={chain.img} alt={chain.chainName} width={32} height={32} />
                                      {chain.chainName}
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </TableCell>
                            <TableCell className="w-[100px] p-3">
                              <div>${truncateToDecimals(Number(item.price), Math.min(Number(item.decimal) || 2, 6))}</div>
                              {!!item.change ? (
                                <span className={`text-xs ${item.change.startsWith('-') ? 'text-red-500' : 'text-green-500'}`}>
                                  {item.change.startsWith('-') ? item.change : '+' + item.change}%
                                </span>
                              ) : (
                                <span className="text-xs text-font-primary/50">0.00%</span>
                              )}
                            </TableCell>
                            <TableCell className="w-[150px] p-3">$ {smartFormat(item.liquidity)}</TableCell>
                            <TableCell className="w-[100px] p-3">$ {smartFormat(item.marketCap)}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        // 搜索结果为空时，显示空状态
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-sm text-font-primary/50 py-8">
                            <Empty>{t('empty.noData')}</Empty>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
